import { FEATURES, hasFeature } from '../lib/plans';
import { useState } from 'react';
import { setMyBrand, updateMyOrg, uploadOrgLogo, setMyReplyTo, sendOrgMail, exportMyOrg, downloadJson } from '../lib/org';

const ZONES = [
  ['America/Argentina/Buenos_Aires', 'Argentina (Buenos Aires)'], ['America/Montevideo', 'Uruguay'],
  ['America/Santiago', 'Chile'], ['America/Asuncion', 'Paraguay'], ['America/La_Paz', 'Bolivia'],
  ['America/Lima', 'Perú'], ['America/Bogota', 'Colombia'], ['America/Sao_Paulo', 'Brasil (São Paulo)'],
  ['America/Mexico_City', 'México'], ['America/New_York', 'Estados Unidos (Este)'], ['Europe/Madrid', 'España'],
];

import MailDomain from './MailDomain';

// Datos de la organización, plan y uso. Solo para administradores.
export default function OrgSettings({ org, onSaved, showToast }) {
  const [name, setName] = useState(org?.name || '');
  const [sender, setSender] = useState(org?.sender_name || '');
  const [tz, setTz] = useState(org?.timezone || 'America/Argentina/Buenos_Aires');
  const [saving, setSaving] = useState(false);
  const [reply, setReply] = useState(org?.reply_to || '');
  const [testing, setTesting] = useState(false);
  const [logo, setLogo] = useState(org?.logo_url || null);
  const [color, setColor] = useState(org?.brand_color || '');
  const [hideProd, setHideProd] = useState(!!org?.hide_product_brand);
  if (!org) return null;
  const brandOk = hasFeature(org.features, 'brand_custom');

  const max = org.max_employees;
  const pct = max ? Math.min(100, Math.round((org.employees / max) * 100)) : 0;

  const save = async () => {
    setSaving(true);
    try {
      await setMyReplyTo(reply);
      await updateMyOrg({ name, senderName: sender, timezone: tz, logoUrl: logo });
      await setMyBrand({ color, hideProduct: hideProd });
      showToast?.('Organización actualizada');
      await onSaved?.();
    } catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setSaving(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try { await sendOrgMail('test'); showToast?.('Te enviamos un mail de prueba'); }
    catch (e) { showToast?.(e.message || 'No se pudo enviar', 'error'); }
    setTesting(false);
  };

  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    try { setLogo(await uploadOrgLogo(org.id, f)); showToast?.('Logo cargado: tocá Guardar para aplicarlo'); }
    catch (err) { showToast?.(err.message || 'No se pudo subir el logo', 'error'); }
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-bold text-gray-900 text-sm">Mi organización</h3>
          <p className="text-xs text-gray-400 mt-0.5">Estos datos aparecen en recibos, reportes y mails</p>
        </div>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100">Plan {org.plan_name}</span>
      </div>

      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Personas activas</span><b className="text-gray-800">{org.employees}{max ? ` de ${max}` : ' (sin tope)'}</b>
        </div>
        {max && <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 90 ? '#dc2626' : '#2f3d7a' }} /></div>}
        {max && pct >= 90 && <p className="text-xs text-red-500 mt-1.5">Estás por llegar al límite de tu plan.</p>}
      </div>

      <details className="rounded-xl border border-gray-100 px-3 py-2">
        <summary className="text-xs font-bold text-gray-600 cursor-pointer">Qué incluye tu plan{org.max_locations ? ` · hasta ${org.max_locations} sede(s)` : ' · sedes sin tope'}</summary>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {FEATURES.map((f) => { const on = hasFeature(org.features, f.key); return (
            <p key={f.key} className={`text-xs flex gap-2 ${on ? 'text-gray-700' : 'text-gray-300'}`}><span className={on ? 'text-emerald-500' : ''}>{on ? '✓' : '—'}</span>{f.label}</p>
          ); })}
        </div>
      </details>

      {org.slug && (
        <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-500">
          Enlace de ingreso de tu equipo (con tu logo):
          <div className="flex items-center justify-between gap-2 mt-1">
            <b className="text-gray-800 truncate">{window.location.origin}/o/{org.slug}</b>
            <button onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/o/${org.slug}`).then(() => showToast?.('Enlace copiado'))} className="font-bold text-sky-700">Copiar</button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-2xl border border-gray-100 bg-gray-50 flex items-center justify-center overflow-hidden">
          {logo ? <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-gray-400">Sin logo</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          {brandOk ? <label className="text-xs font-bold text-sky-700 cursor-pointer">Subir logo
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" />
          </label> : <span className="text-[11px] text-gray-400">El logo propio no está incluido en tu plan.</span>}
          {logo && <button type="button" onClick={() => setLogo(null)} className="text-xs text-red-500 text-left">Quitar logo</button>}
          <span className="text-[10px] text-gray-400">Aparece en recibos, reportes y encabezados</span>
        </div>
      </div>
      <label className="block text-xs font-semibold text-gray-500">Nombre
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
      </label>
      <label className="block text-xs font-semibold text-gray-500">Nombre del remitente de los mails
        <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder={name} className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
      </label>
      <label className="block text-xs font-semibold text-gray-500">Mail de respuesta (opcional)
        <input type="email" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="contacto@tuorganizacion.com" className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
        <span className="block text-[10px] font-normal text-gray-400 mt-1">Los mails salen con el nombre de tu organización; si alguien responde, la respuesta llega a esta dirección.</span>
      </label>
      <label className="block text-xs font-semibold text-gray-500">Zona horaria
        <select value={tz} onChange={(e) => setTz(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white">
          {ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <button onClick={save} disabled={saving || name.trim().length < 2} className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
      <button onClick={sendTest} disabled={testing} className="w-full py-2 rounded-xl text-xs font-bold text-sky-700 border border-sky-100 disabled:opacity-50">
        {testing ? 'Enviando…' : 'Enviarme un mail de prueba'}
      </button>
      {brandOk && <div className="rounded-2xl border border-gray-100 p-4 space-y-3">
        <div><p className="text-sm font-bold text-gray-800">Colores y marca</p><p className="text-xs text-gray-400">Tus empleados ven tu empresa, no nuestro producto.</p></div>
        <div className="flex items-center gap-3">
          <input type="color" value={color || '#222d5a'} onChange={(e) => setColor(e.target.value)} className="w-12 h-10 rounded-lg border border-gray-200 bg-white cursor-pointer" />
          <div className="flex-1"><p className="text-xs font-bold text-gray-600">Color principal</p><p className="text-[11px] text-gray-400">Botones, resaltados y barra del celular. Se aplica al guardar.</p></div>
          {color && <button type="button" onClick={() => setColor('')} className="text-xs font-bold text-gray-400">Volver al original</button>}
        </div>
        {hasFeature(org.features, 'white_label')
          ? <label className="flex items-start gap-2 text-sm text-gray-600"><input type="checkbox" className="mt-1" checked={hideProd} onChange={(e) => setHideProd(e.target.checked)} /><span>Ocultar la marca de Gestión360<span className="block text-[11px] text-gray-400">Quita los enlaces y leyendas del producto en el ingreso y en los comprobantes.</span></span></label>
          : <p className="text-[11px] text-gray-400 bg-gray-50 rounded-xl px-3 py-2">Ocultar la marca de Gestión360 está disponible en los planes Business y Total.</p>}
      </div>}
      <MailDomain showToast={showToast} />
      <button onClick={async () => { try { downloadJson(await exportMyOrg(), 'respaldo-gestion360'); } catch (e) { alert(e.message || 'No se pudo generar el respaldo'); } }} className="w-full py-2 rounded-xl text-xs font-bold text-gray-600 border border-gray-200">
        Descargar respaldo de todos mis datos
      </button>
    </div>
  );
}
